/**
 * Contractor Dashboard V2 - MaintainX Style with Sidebar
 * Enterprise-grade contractor portal with backorder pipeline integration
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import {
  Wrench,
  Briefcase,
  Clock,
  CheckCircle2,
  DollarSign,
  FileText,
  MapPin,
  AlertTriangle,
  Loader2,
  ChevronRight,
  Building2,
  Calendar,
  Shield,
  Users,
  UserPlus,
  User,
  Trash2,
  Edit,
  Mail,
  Phone,
  Eye,
  Settings,
  Package,
  Star,
  RefreshCw,
  TrendingUp,
  Award,
  ShieldAlert
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import ContractorLayout from "@/components/layouts/ContractorLayout";
import PortalInvoiceSubmit from "@/components/portal/PortalInvoiceSubmit";
import BackorderPipeline from "@/components/BackorderPipeline";
import TelegramConnect from "@/components/TelegramConnect";
import TelegramInviteBanner from "@/components/TelegramInviteBanner";
import ContractorSettingsModal from "@/components/ContractorSettingsModal";
import DashboardHeroBanner, { DashboardSearchBar } from "@/components/ui/DashboardHeroBanner";
import ContractorNotificationsPanel from "@/components/contractor/ContractorNotificationsPanel";
import ContractorRfqsBoard from "@/components/contractor/ContractorRfqsBoard";
import ContractorWorkOrdersBoard from "@/components/contractor/ContractorWorkOrdersBoard";
import PublicSiteHeader from "@/components/PublicSiteHeader";
import {
  HomeHeroV2,
  TrustMarquee,
  MarriageBento,
  SolutionsGrid,
  MetricsBand,
  IndustriesWeServe,
  NewsAndInsights,
} from "@/components/HomeHeroV2";

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, color = "blue" }) => {
  const colorStyles = {
    blue: "bg-blue-50 text-blue-600",
    amber: "bg-amber-50 text-amber-600",
    emerald: "bg-emerald-50 text-emerald-600",
    purple: "bg-purple-50 text-purple-600",
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorStyles[color]}`}>
          <Icon size={20} />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value?.toLocaleString() || 0}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
};

// Job Card Component
const JobCard = ({ job, onViewDetails }) => {
  const statusColors = {
    pending: "bg-amber-100 text-amber-700",
    assigned: "bg-blue-100 text-blue-700",
    in_progress: "bg-purple-100 text-purple-700",
    completed: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-red-100 text-red-700"
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div>
          <span className="text-slate-400 text-sm font-mono">#{job.id?.substring(0, 8) || "N/A"}</span>
          <p className="text-slate-900 font-medium">{job.issue_type?.replace(/_/g, ' ') || job.type || "Work Order"}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[job.status] || statusColors.pending}`}>
          {job.status?.replace(/_/g, ' ').toUpperCase() || "PENDING"}
        </span>
      </div>
      
      <div className="space-y-2 text-sm text-slate-600 mb-4">
        {job.hospital_name && (
          <div className="flex items-center gap-2">
            <Building2 size={14} className="text-slate-400" />
            <span>{job.hospital_name}</span>
          </div>
        )}
        {job.facility_name && (
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-slate-400" />
            <span>{job.facility_name}</span>
          </div>
        )}
        {job.scheduled_date && (
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-slate-400" />
            <span>{new Date(job.scheduled_date).toLocaleDateString()}</span>
          </div>
        )}
      </div>
      
      <Button 
        onClick={() => onViewDetails(job)}
        variant="outline"
        className="w-full"
      >
        <Eye size={16} className="mr-2" />
        View Details
      </Button>
    </div>
  );
};

export default function ContractorDashboardV2() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "home";
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showSettings, setShowSettings] = useState(false);
  
  // Team Management State
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState({ 
    name: "", 
    email: "", 
    phone: "", 
    role: "field_tech",
    access_level: "view_only",
    can_submit_certificates: false,
    can_view_billing: false,
    can_manage_team: false
  });
  const [savingMember, setSavingMember] = useState(false);
  const [verification, setVerification] = useState(null);
  const prevCompleteRef = useRef(null);

  const token = localStorage.getItem("contractor_token");

  useEffect(() => {
    if (!token) {
      navigate("/contractor/login");
      return;
    }
    fetchDashboard();
    fetchVerification();
  }, [navigate, token]);

  // Auto-celebrate when contractor reaches 100% doc completion
  useEffect(() => {
    if (!verification?.checklist?.length) return;
    const total = verification.checklist.length;
    const uploaded = verification.checklist.filter((c) => c.uploaded).length;
    const isComplete = uploaded === total;

    if (prevCompleteRef.current === null) {
      // First read after login — just record, don't celebrate
      prevCompleteRef.current = isComplete;
      return;
    }

    if (!prevCompleteRef.current && isComplete) {
      toast.success(
        "🎉 All documents uploaded! Sterile Mate admin will review and verify your account shortly.",
        { duration: 7000 }
      );
    }
    prevCompleteRef.current = isComplete;
  }, [verification]);

  // Update tab from URL params
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/contractor/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("contractor_token");
        localStorage.removeItem("contractor_data");
        navigate("/contractor/login");
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchVerification = async () => {
    try {
      const res = await axios.get(`${API}/contractor/me/verification-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerification(res.data);
    } catch (e) { /* ignore — non-critical */ }
  };

  const fetchTeamMembers = async () => {
    setLoadingTeam(true);
    try {
      const res = await axios.get(`${API}/contractor/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(res.data.team_members || []);
    } catch (err) {
      console.error("Failed to load team:", err);
    } finally {
      setLoadingTeam(false);
    }
  };

  useEffect(() => {
    if (activeTab === "team" && token) {
      fetchTeamMembers();
    }
  }, [activeTab, token]);

  const handleLogout = () => {
    localStorage.removeItem("contractor_token");
    localStorage.removeItem("contractor_data");
    navigate("/contractor/login");
    toast.success("Logged out successfully");
  };

  const handleViewJobDetails = (job) => {
    // Navigate to job detail page or open modal
    if (job.job_token) {
      navigate(`/contractor/job/${job.job_token}`);
    } else {
      toast.info("Job details view coming soon");
    }
  };

  const handleSaveMember = async () => {
    if (!memberForm.name || !memberForm.email) {
      toast.error("Name and email are required");
      return;
    }
    
    setSavingMember(true);
    try {
      if (editingMember) {
        await axios.put(`${API}/contractor/team/${editingMember.id}`, memberForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Team member updated");
      } else {
        await axios.post(`${API}/contractor/team`, memberForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Team member added");
      }
      setShowAddMember(false);
      setEditingMember(null);
      setMemberForm({ 
        name: "", email: "", phone: "", role: "field_tech",
        access_level: "view_only", can_submit_certificates: false,
        can_view_billing: false, can_manage_team: false
      });
      fetchTeamMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save team member");
    } finally {
      setSavingMember(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!confirm("Are you sure you want to remove this team member?")) return;
    
    try {
      await axios.delete(`${API}/contractor/team/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Team member removed");
      fetchTeamMembers();
    } catch (err) {
      toast.error("Failed to remove team member");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const contractor = data?.contractor || {};
  const stats = data?.stats || {};
  // Jobs come as an object with new, in_progress, completed arrays - flatten them
  const jobsData = data?.jobs || {};
  const jobs = [
    ...(jobsData.new || []),
    ...(jobsData.in_progress || []),
    ...(jobsData.completed || [])
  ];
  const invoices = data?.invoices || [];

  // Get title based on active tab
  const getPageTitle = () => {
    switch (activeTab) {
      case "home": return "Welcome";
      case "overview": return "Dashboard Overview";
      case "rfqs": return "My RFQs";
      case "jobs": return "My Jobs";
      case "job-board": return "Job Board";
      case "backorders": return "Backorder Pipeline";
      case "earnings": return "Earnings";
      case "invoices": return "Invoices";
      case "team": return "My Team";
      case "certification": return "Certification";
      case "settings": return "Settings";
      default: return "Dashboard";
    }
  };

  const isHomeTab = activeTab === "home";

  return (
    <ContractorLayout
      title={getPageTitle()}
      subtitle={`Welcome back, ${contractor?.company_name || contractor?.business_name || "Contractor"}`}
      contractor={contractor}
      onLogout={handleLogout}
      hideTopHeader={isHomeTab}
      mainPadding={isHomeTab ? "p-0" : "p-4 sm:p-6"}
      actions={
        <div className="flex items-center gap-2">
          <TelegramConnect token={token} userType="contractor" />
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowSettings(true)}
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      }
    >
      {isHomeTab ? (
        <div className="space-y-0" data-testid="contractor-home-content">
          <PublicSiteHeader />
          {/* Personalized welcome banner */}
          {(() => {
            const checklist = verification?.checklist || [];
            const total = checklist.length;
            const uploadedCount = checklist.filter((c) => c.uploaded).length;
            const outstandingDocs = checklist.filter((c) => !c.uploaded);
            const hasOutstanding = outstandingDocs.length > 0;
            const awaitingReview = verification && !verification.is_verified && verification.all_uploaded;
            const pct = total > 0 ? Math.round((uploadedCount / total) * 100) : 0;
            const ringColor = verification?.is_verified ? "#34d399" : pct === 100 ? "#34d399" : "#fbbf24";
            const ringTrack = "rgba(255,255,255,0.12)";
            const radius = 12;
            const circumference = 2 * Math.PI * radius;
            const dashOffset = circumference * (1 - pct / 100);
            return (
              <div className="bg-gradient-to-r from-[#0a0f1a] via-[#0b1d3a] to-[#0a0f1a] border-b border-emerald-400/20" data-testid="contractor-welcome-banner">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    {total > 0 ? (
                      <div
                        className="hidden sm:inline-flex relative w-9 h-9 mt-0.5 shrink-0 items-center justify-center"
                        title={`${uploadedCount} of ${total} documents uploaded`}
                        data-testid="contractor-welcome-progress-ring"
                        data-progress={pct}
                      >
                        <svg className="w-9 h-9 -rotate-90" viewBox="0 0 32 32" aria-hidden="true">
                          <circle cx="16" cy="16" r={radius} fill="transparent" stroke={ringTrack} strokeWidth="3" />
                          <circle
                            cx="16"
                            cy="16"
                            r={radius}
                            fill="transparent"
                            stroke={ringColor}
                            strokeWidth="3"
                            strokeDasharray={circumference}
                            strokeDashoffset={dashOffset}
                            strokeLinecap="round"
                            style={{ transition: "stroke-dashoffset 600ms ease" }}
                          />
                        </svg>
                        <span
                          className="absolute inset-0 flex items-center justify-center text-[10px] font-bold"
                          style={{ color: ringColor }}
                        >
                          {pct}%
                        </span>
                      </div>
                    ) : (
                      <span className="hidden sm:inline-flex w-7 h-7 mt-0.5 rounded-full bg-emerald-400/15 border border-emerald-400/40 items-center justify-center text-emerald-300 text-xs font-bold shrink-0">
                        {(contractor?.company_name || "C").charAt(0).toUpperCase()}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-200 truncate">
                        Welcome back,{" "}
                        <span className="font-semibold text-white" data-testid="contractor-welcome-name">
                          {contractor?.company_name || contractor?.business_name || "Contractor"}
                        </span>
                        {total > 0 && !verification?.is_verified && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium text-slate-300 align-middle"
                            data-testid="contractor-welcome-progress-text"
                          >
                            · {uploadedCount} of {total} documents uploaded
                          </span>
                        )}
                      </p>
                      {verification && (
                        verification.is_verified ? (
                          <p className="mt-0.5 text-xs text-emerald-300 inline-flex items-center gap-1.5" data-testid="contractor-welcome-verified">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Account Verified — all documentation complete.
                          </p>
                        ) : hasOutstanding ? (
                          <div className="mt-1.5" data-testid="contractor-welcome-outstanding-docs">
                            <p className="text-xs text-amber-300 inline-flex items-center gap-1.5 font-semibold">
                              <ShieldAlert className="w-3.5 h-3.5" />
                              {outstandingDocs.length} outstanding {outstandingDocs.length === 1 ? "document" : "documents"} required — click a chip to upload:
                            </p>
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {outstandingDocs.map((c) => (
                                <label
                                  key={c.type}
                                  className="text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 bg-amber-400/10 border border-amber-300/40 text-amber-200 hover:bg-amber-400/20 hover:text-amber-100 cursor-pointer transition-colors"
                                  data-testid={`contractor-welcome-doc-${c.type}`}
                                  title={`Upload ${c.name}`}
                                >
                                  <Clock className="w-3 h-3" />
                                  {c.name}
                                  <span className="ml-1 text-[10px] underline">+ Upload</span>
                                  <input
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xlsx,.xls"
                                    onChange={async (e) => {
                                      const f = e.target.files?.[0];
                                      if (!f) return;
                                      const fd = new FormData();
                                      fd.append("file", f);
                                      fd.append("document_type", c.type);
                                      try {
                                        const res = await fetch(`${API}/contractor/me/documents/upload`, {
                                          method: "POST",
                                          headers: { Authorization: `Bearer ${token}` },
                                          body: fd,
                                        });
                                        if (res.ok) {
                                          toast.success(`${c.name} uploaded — pending admin review`);
                                          fetchVerification();
                                        } else {
                                          toast.error("Upload failed");
                                        }
                                      } catch (_err) { toast.error("Upload failed"); }
                                      e.target.value = "";
                                    }}
                                  />
                                </label>
                              ))}
                            </div>
                          </div>
                        ) : awaitingReview ? (
                          <p className="mt-0.5 text-xs text-emerald-300 inline-flex items-center gap-1.5" data-testid="contractor-welcome-awaiting-review">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            All documents uploaded — awaiting Sterile Mate admin review.
                          </p>
                        ) : null
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {verification && !verification.is_verified && awaitingReview && (
                      <button
                        type="button"
                        onClick={() => navigate("/contractor/dashboard?tab=overview")}
                        className="text-xs font-semibold text-emerald-200 hover:text-emerald-100 underline underline-offset-2"
                        data-testid="contractor-welcome-upload-link"
                      >
                        View status →
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => navigate("/contractor/dashboard?tab=rfqs")}
                      className="hidden sm:inline-flex text-xs font-semibold text-emerald-300 hover:text-emerald-200"
                      data-testid="contractor-welcome-rfqs-link"
                    >
                      View my RFQs →
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
          <HomeHeroV2 />
          <TrustMarquee />
          <SolutionsGrid />
          <MarriageBento />
          <MetricsBand />
          <IndustriesWeServe />
          <NewsAndInsights />
        </div>
      ) : (
        <>
          {/* Admin notifications (RFQ invites, awards, follow-ups, rejections) */}
          <ContractorNotificationsPanel token={token} contractor={contractor} />

          {activeTab === "rfqs" ? (
            <ContractorRfqsBoard token={token} contractor={contractor} />
          ) : (
            <>
              {/* Hero Banner */}
              <DashboardHeroBanner 
                portalType="contractor" 
                activeSection={
                  activeTab === "jobs" ? "jobs" :
                  activeTab === "schedule" ? "schedule" :
                  activeTab === "certs" ? "certifications" :
                  activeTab === "earnings" ? "earnings" :
                  activeTab === "team" ? "overview" :
                  "overview"
                }
              />
            </>
          )}

      {/* Verification Status Banner — visible to contractor */}
      {verification && (
        verification.is_verified ? (
          <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between" data-testid="contractor-verified-banner">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <div>
                <p className="font-semibold text-emerald-900">Account Verified</p>
                <p className="text-sm text-emerald-700">You&apos;re fully approved and eligible for new job assignments.</p>
              </div>
            </div>
            {verification.verified_at && (
              <span className="text-xs text-emerald-700 hidden sm:block">Verified {new Date(verification.verified_at).toLocaleDateString()}</span>
            )}
          </div>
        ) : (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="contractor-pending-banner">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-6 h-6 text-amber-600 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-amber-900">Verification Pending</p>
                <p className="text-sm text-amber-700 mb-2">
                  {verification.all_uploaded
                    ? "All documents received. Your account is awaiting admin review — you'll start receiving assignments once Sterile Mate marks you Verified."
                    : "Upload all required documents below. Once everything is in place, an administrator will mark your account Verified."}
                </p>
                {verification.checklist?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {verification.checklist.map((c) => (
                      c.uploaded ? (
                        <span key={c.type} className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 bg-emerald-100 text-emerald-700">
                          <CheckCircle2 className="w-3 h-3" />
                          {c.name}
                        </span>
                      ) : (
                        <label key={c.type} className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 bg-white border border-amber-300 text-amber-700 cursor-pointer hover:bg-amber-100" data-testid={`contractor-upload-${c.type}`}>
                          <Clock className="w-3 h-3" />
                          {c.name}
                          <span className="ml-1 text-[10px] underline">+ Upload</span>
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xlsx,.xls"
                            onChange={async (e) => {
                              const f = e.target.files?.[0];
                              if (!f) return;
                              const fd = new FormData();
                              fd.append("file", f);
                              fd.append("document_type", c.type);
                              try {
                                const res = await fetch(`${API}/contractor/me/documents/upload`, {
                                  method: "POST",
                                  headers: { Authorization: `Bearer ${token}` },
                                  body: fd,
                                });
                                if (res.ok) {
                                  toast.success(`${c.name} uploaded — pending admin review`);
                                  fetchVerification();
                                } else {
                                  toast.error("Upload failed");
                                }
                              } catch (_err) { toast.error("Upload failed"); }
                              e.target.value = "";
                            }}
                          />
                        </label>
                      )
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      )}
      
      {/* Search Bar Below Banner */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <DashboardSearchBar 
          placeholder="Search jobs, invoices, team..."
          className="w-full sm:w-auto"
        />
      </div>
      
      {/* Telegram Invite Banner */}
      <TelegramInviteBanner 
        token={token} 
        userType="contractor" 
        userName={contractor?.owner_name?.split(' ')[0] || contractor?.business_name?.split(' ')[0]}
      />

      {/* Stats Row - Only show on jobs tab */}
      {activeTab === "jobs" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatsCard icon={Briefcase} label="Active Jobs" value={data?.job_counts?.in_progress || stats.active_jobs || 0} color="blue" />
          <StatsCard icon={Clock} label="New" value={data?.job_counts?.new || stats.pending_jobs || 0} color="amber" />
          <StatsCard icon={CheckCircle2} label="Completed" value={data?.job_counts?.completed || stats.completed_jobs || 0} color="emerald" />
          <StatsCard icon={DollarSign} label="Total Earned" value={`$${(data?.earnings?.total || stats.total_earned || 0).toLocaleString()}`} color="purple" />
        </div>
      )}

      {/* Tab Content */}
      {activeTab === "jobs" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Your Jobs</h3>
            <Button variant="outline" size="sm" onClick={fetchDashboard}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {jobs.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-slate-900 font-medium mb-2">No jobs assigned</h3>
              <p className="text-slate-500">You&apos;ll see new job assignments here.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {jobs.map((job) => (
                <JobCard key={job.id} job={job} onViewDetails={handleViewJobDetails} />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "work-orders" && (
        <ContractorWorkOrdersBoard token={token} />
      )}

      {activeTab === "backorders" && (
        <BackorderPipeline 
          viewMode="admin" 
          onRefresh={fetchDashboard}        />
      )}

      {activeTab === "invoices" && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <PortalInvoiceSubmit token={token} />
          </div>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Your Invoices</h3>
            <Button variant="outline" size="sm" onClick={fetchDashboard}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Invoice #</th>
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Job</th>
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Amount</th>
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Status</th>
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Date</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-slate-500">
                      No invoices yet
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-4 text-slate-900 font-mono">#{invoice.invoice_number || invoice.id?.substring(0, 8)}</td>
                      <td className="p-4 text-slate-600">{invoice.job_description || "Work Order"}</td>
                      <td className="p-4 text-emerald-600 font-mono font-medium">${invoice.amount?.toFixed(2)}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          invoice.status === "paid" ? "bg-emerald-100 text-emerald-700" :
                          invoice.status === "pending" ? "bg-amber-100 text-amber-700" :
                          "bg-slate-100 text-slate-600"
                        }`}>
                          {invoice.status?.toUpperCase() || "PENDING"}
                        </span>
                      </td>
                      <td className="p-4 text-slate-500 text-sm">
                        {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          </div>
        </div>
      )}

      {activeTab === "team" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Team Members</h3>
            <Button onClick={() => setShowAddMember(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Member
            </Button>
          </div>
          
          {loadingTeam ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : teamMembers.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-slate-900 font-medium mb-2">No team members</h3>
              <p className="text-slate-500 mb-4">Add team members to help manage your jobs.</p>
              <Button onClick={() => setShowAddMember(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                Add Your First Team Member
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teamMembers.map((member) => (
                <div key={member.id} className="bg-white border border-slate-200 rounded-xl p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{member.name}</p>
                        <p className="text-sm text-slate-500">{member.role?.replace(/_/g, ' ')}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-1 text-sm text-slate-600 mb-4">
                    <div className="flex items-center gap-2">
                      <Mail size={14} className="text-slate-400" />
                      <span>{member.email}</span>
                    </div>
                    {member.phone && (
                      <div className="flex items-center gap-2">
                        <Phone size={14} className="text-slate-400" />
                        <span>{member.phone}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        setEditingMember(member);
                        setMemberForm({
                          name: member.name,
                          email: member.email,
                          phone: member.phone || "",
                          role: member.role || "field_tech",
                          access_level: member.access_level || "view_only",
                          can_submit_certificates: member.can_submit_certificates || false,
                          can_view_billing: member.can_view_billing || false,
                          can_manage_team: member.can_manage_team || false
                        });
                        setShowAddMember(true);
                      }}
                    >
                      <Edit size={14} className="mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteMember(member.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "settings" && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Account Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-600">Company Name</label>
              <p className="text-slate-900 font-medium">{contractor?.company_name || contractor?.business_name}</p>
            </div>
            <div>
              <label className="text-sm text-slate-600">Owner</label>
              <p className="text-slate-900">{contractor?.owner_name}</p>
            </div>
            <div>
              <label className="text-sm text-slate-600">Email</label>
              <p className="text-slate-900">{contractor?.email}</p>
            </div>
            <div className="flex gap-3 mt-4">
              <Button onClick={() => setShowSettings(true)}>
                Edit Settings
              </Button>
              <Link to="/contractor/profile">
                <Button variant="outline">
                  <Shield className="w-4 h-4 mr-2" />
                  View Profile
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* ========== EARNINGS TAB ========== */}
      {activeTab === "earnings" && (
        <div className="space-y-6" data-testid="earnings-tab">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <DollarSign className="w-6 h-6 text-emerald-600 mb-2" />
              <p className="text-2xl font-bold text-slate-900">${(data?.earnings?.total || 0).toLocaleString()}</p>
              <p className="text-sm text-slate-500">Total Earned (All Time)</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <DollarSign className="w-6 h-6 text-blue-600 mb-2" />
              <p className="text-2xl font-bold text-slate-900">${(data?.earnings?.this_month || 0).toLocaleString()}</p>
              <p className="text-sm text-slate-500">This Month</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <Clock className="w-6 h-6 text-amber-600 mb-2" />
              <p className="text-2xl font-bold text-slate-900">${(data?.earnings?.pending || 0).toLocaleString()}</p>
              <p className="text-sm text-slate-500">Pending Payment</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 mb-2" />
              <p className="text-2xl font-bold text-slate-900">{data?.earnings?.jobs_completed || stats.completed_jobs || 0}</p>
              <p className="text-sm text-slate-500">Jobs Completed</p>
            </div>
          </div>

          {/* Earnings History */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Earnings History</h3>
              <p className="text-sm text-slate-500">Track your income through the Sterile Mate network</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left p-4 text-slate-600 font-medium text-sm">Description</th>
                    <th className="text-left p-4 text-slate-600 font-medium text-sm">Facility</th>
                    <th className="text-left p-4 text-slate-600 font-medium text-sm">Amount</th>
                    <th className="text-left p-4 text-slate-600 font-medium text-sm">Status</th>
                    <th className="text-left p-4 text-slate-600 font-medium text-sm">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoices || []).length === 0 ? (
                    <tr><td colSpan={5} className="p-8 text-center text-slate-500">Complete your first job to start earning</td></tr>
                  ) : (
                    invoices.map((inv) => (
                      <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-4 text-slate-900">{inv.job_description || "Work Order"}</td>
                        <td className="p-4 text-slate-600">{inv.hospital_name || "—"}</td>
                        <td className="p-4 text-emerald-600 font-bold">${inv.amount?.toFixed(2)}</td>
                        <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-medium ${inv.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{inv.status?.toUpperCase() || "PENDING"}</span></td>
                        <td className="p-4 text-slate-500 text-sm">{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========== JOB BOARD TAB ========== */}
      {activeTab === "job-board" && (
        <div className="space-y-4" data-testid="job-board-tab">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Available Jobs</h3>
              <p className="text-sm text-slate-500">Open jobs from healthcare facilities in your area. Bid to win.</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchDashboard}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          {(data?.available_jobs || []).length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-slate-900 font-medium mb-2">No open jobs right now</h3>
              <p className="text-slate-500 mb-4">New jobs are posted daily. Check back soon or enable Telegram alerts for instant notifications.</p>
              <TelegramConnect token={token} userType="contractor" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(data?.available_jobs || []).map((job) => (
                <div key={job.id} className="bg-white border border-emerald-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-slate-900 font-medium">{job.issue_type?.replace(/_/g, ' ') || job.type || "Work Order"}</p>
                      <span className="text-xs text-slate-400">{job.posted_at ? `Posted ${new Date(job.posted_at).toLocaleDateString()}` : "Recently posted"}</span>
                    </div>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">OPEN</span>
                  </div>
                  <div className="space-y-2 text-sm text-slate-600 mb-4">
                    {job.hospital_name && <div className="flex items-center gap-2"><Building2 size={14} className="text-slate-400" /><span>{job.hospital_name}</span></div>}
                    {job.facility_name && <div className="flex items-center gap-2"><MapPin size={14} className="text-slate-400" /><span>{job.facility_name}</span></div>}
                    {job.urgency && <div className="flex items-center gap-2"><Clock size={14} className="text-slate-400" /><span className="capitalize">{job.urgency}</span></div>}
                  </div>
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleViewJobDetails(job)}>
                    View & Bid
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== CERTIFICATION TAB ========== */}
      {activeTab === "certification" && (
        <div className="space-y-6" data-testid="certification-tab">
          {/* Current Tier */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Your Certification Status</h3>
            <div className="flex items-center gap-6 mb-6">
              <div className={`w-20 h-20 rounded-xl flex items-center justify-center ${
                contractor?.tier === "fortress" ? "bg-cyan-100" :
                contractor?.tier === "gold" ? "bg-amber-100" :
                contractor?.tier === "silver" ? "bg-slate-200" : "bg-amber-50"
              }`}>
                <Star className={`w-10 h-10 ${
                  contractor?.tier === "fortress" ? "text-cyan-600" :
                  contractor?.tier === "gold" ? "text-amber-500" :
                  contractor?.tier === "silver" ? "text-slate-500" : "text-amber-700"
                }`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 capitalize">{contractor?.tier || "Bronze"}</p>
                <p className="text-sm text-slate-500">{
                  contractor?.tier === "fortress" ? "Elite — First access to all premium jobs" :
                  contractor?.tier === "gold" ? "Premium rates and priority job matching" :
                  contractor?.tier === "silver" ? "Priority job alerts and enhanced visibility" :
                  "Welcome to the network — complete jobs to level up"
                }</p>
              </div>
            </div>
          </div>

          {/* Tier Progress */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Certification Tiers</h3>
            <div className="space-y-4">
              {[
                { tier: "Bronze", jobs: 0, desc: "New member — standard job access", color: "bg-amber-100 text-amber-700 border-amber-200", starColor: "text-amber-700", active: !contractor?.tier || contractor?.tier === "bronze" },
                { tier: "Silver", jobs: 5, desc: "5+ completed jobs — priority alerts, enhanced profile visibility", color: "bg-slate-100 text-slate-600 border-slate-200", starColor: "text-slate-500", active: contractor?.tier === "silver" },
                { tier: "Gold", jobs: 20, desc: "20+ completed jobs — premium rates, first access to high-value jobs", color: "bg-amber-50 text-amber-600 border-amber-200", starColor: "text-amber-500", active: contractor?.tier === "gold" },
                { tier: "Fortress Certified", jobs: 50, desc: "50+ jobs, all credentials verified — elite status, maximum earning potential", color: "bg-cyan-50 text-cyan-700 border-cyan-200", starColor: "text-cyan-600", active: contractor?.tier === "fortress" },
              ].map((t) => {
                const completed = data?.earnings?.jobs_completed || stats.completed_jobs || 0;
                const isUnlocked = completed >= t.jobs;
                return (
                  <div key={t.tier} className={`flex items-center gap-4 p-4 rounded-xl border ${t.active ? t.color + " border-2" : isUnlocked ? "bg-white border-slate-200" : "bg-slate-50 border-slate-100 opacity-60"}`}>
                    <Star className={`w-6 h-6 ${t.active ? t.starColor : isUnlocked ? "text-emerald-500" : "text-slate-300"}`} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className={`font-bold ${t.active ? "" : "text-slate-700"}`}>{t.tier}</p>
                        <span className="text-xs text-slate-500">{t.jobs === 0 ? "Entry" : `${t.jobs}+ jobs`}</span>
                      </div>
                      <p className="text-sm text-slate-500">{t.desc}</p>
                    </div>
                    {t.active && <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full">CURRENT</span>}
                    {!t.active && isUnlocked && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Referral Section */}
          <div className="bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Refer & Earn</h3>
            <p className="text-sm text-slate-600 mb-4">Share your referral code with other contractors and suppliers. When they complete their first job, you both benefit.</p>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-white border border-slate-200 rounded-lg px-4 py-3 font-mono text-sm text-slate-900">
                {contractor?.referral_code || contractor?.email?.split("@")[0]?.toUpperCase() || "YOUR-CODE"}
              </div>
              <Button variant="outline" onClick={() => {
                const code = contractor?.referral_code || contractor?.email?.split("@")[0]?.toUpperCase();
                navigator.clipboard.writeText(`https://sterilemate.com/join-network?ref=${code}`);
                toast.success("Referral link copied!");
              }}>Copy Link</Button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Team Member Dialog */}
      <Dialog open={showAddMember} onOpenChange={(open) => {
        setShowAddMember(open);
        if (!open) {
          setEditingMember(null);
          setMemberForm({ 
            name: "", email: "", phone: "", role: "field_tech",
            access_level: "view_only", can_submit_certificates: false,
            can_view_billing: false, can_manage_team: false
          });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingMember ? "Edit Team Member" : "Add Team Member"}</DialogTitle>
            <DialogDescription>
              {editingMember ? "Update team member information" : "Add a new member to your team"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input 
                value={memberForm.name}
                onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
                placeholder="John Smith"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input 
                type="email"
                value={memberForm.email}
                onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input 
                value={memberForm.phone}
                onChange={(e) => setMemberForm({ ...memberForm, phone: e.target.value })}
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={memberForm.role} onValueChange={(v) => setMemberForm({ ...memberForm, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="field_tech">Field Tech</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-3 pt-2 border-t">
              <Label className="text-slate-900">Permissions</Label>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Can submit certificates</span>
                <Switch 
                  checked={memberForm.can_submit_certificates}
                  onCheckedChange={(v) => setMemberForm({ ...memberForm, can_submit_certificates: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Can view billing</span>
                <Switch 
                  checked={memberForm.can_view_billing}
                  onCheckedChange={(v) => setMemberForm({ ...memberForm, can_view_billing: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Can manage team</span>
                <Switch 
                  checked={memberForm.can_manage_team}
                  onCheckedChange={(v) => setMemberForm({ ...memberForm, can_manage_team: v })}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMember(false)}>Cancel</Button>
            <Button onClick={handleSaveMember} disabled={savingMember}>
              {savingMember && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingMember ? "Update" : "Add Member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <ContractorSettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        contractor={contractor}
        token={token}
        onUpdate={fetchDashboard}
      />
        </>
      )}
    </ContractorLayout>
  );
}
